/**
 * TaxPage — UK tax planning dashboard
 *
 * Consumes the existing /api/tax endpoints (wrapper-summary, allowances,
 * cgt-estimate, cgt-planner) to give Simon a single page that answers:
 * - Where's my money sitting by tax wrapper?
 * - What allowances do I still have for this tax year?
 * - Unrealised CGT exposure?
 * - Bed-and-ISA opportunities?
 */
import { useEffect, useState } from 'react'
import { Calculator, RefreshCw, TrendingUp, AlertTriangle, PiggyBank, Receipt } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '@/lib/api'
import TaxWorkspaceTabs from '@/components/ui/TaxWorkspaceTabs'
import PageHeader from '@/components/ui/PageHeader'

interface WrapperSummary {
  total_value: number
  tax_year: string
  wrappers: {
    wrapper: string
    value: number
    pct: number
    accounts: string[]
    description: string
    annual_allowance: number | null
    allowance_label: string
    notes: string
  }[]
}

interface Allowance {
  name: string
  allowance_key?: string
  annual_limit: number
  used: number
  remaining: number
  description: string
  wrapper: string
  current_isa_value?: number
  current_sipp_value?: number
  note?: string | null
}

interface AllowancesResponse {
  tax_year: string
  allowances: Allowance[]
}

interface CgtEstimate {
  tax_year: string
  cgt_allowance: number
  total_unrealised_gain: number
  taxable_gain_after_allowance: number
  estimated_tax_basic_rate: number
  estimated_tax_higher_rate: number
  applicable_cgt_rate_pct?: number
  applicable_band?: string
  applicable_rate_basis?: string
  estimated_tax_applicable?: number
  cgt_rate_basic: number
  cgt_rate_higher: number
  note: string
  accounts: {
    account_id: number
    account_name: string
    wrapper: string
    cost_basis: number
    current_value: number
    unrealised_gain: number
    holdings: any[]
  }[]
}

const fmt = (n: number) =>
  `£${n.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`

const fmtSigned = (n: number) =>
  `${n >= 0 ? '+' : ''}£${Math.abs(n).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`

const WRAPPER_COLOURS: Record<string, string> = {
  ISA:    'border-emerald/30 bg-emerald/5',
  SIPP:   'border-blue/30 bg-blue/5',
  GIA:    'border-amber/30 bg-amber/5',
  Crypto: 'border-purple/30 bg-purple/5',
  Cash:   'border-border-light bg-surface-hover/40',
}

export default function TaxPage() {
  const [wrappers, setWrappers] = useState<WrapperSummary | null>(null)
  const [allowances, setAllowances] = useState<AllowancesResponse | null>(null)
  const [cgt, setCgt] = useState<CgtEstimate | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchAll = async () => {
    setLoading(true)
    setError(null)
    try {
      const [w, a, c] = await Promise.all([
        api.get<WrapperSummary>('/api/tax/wrapper-summary'),
        api.get<AllowancesResponse>('/api/tax/allowances'),
        api.get<CgtEstimate>('/api/tax/cgt-estimate'),
      ])
      setWrappers(w.data)
      setAllowances(a.data)
      setCgt(c.data)
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load tax data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAll()
  }, [])

  if (loading && !wrappers) {
    return (
      <div className="space-y-4 animate-fade-in">
        <div className="h-8 w-64 skeleton" />
        <div className="h-32 skeleton rounded-xl" />
        <div className="h-64 skeleton rounded-xl" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="card p-6 text-center">
        <p className="text-rose-light">{error}</p>
        <button type="button" onClick={fetchAll} className="btn-secondary mt-3">Retry</button>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title={`Tax Planning · UK ${wrappers?.tax_year ?? '2025/26'}`}
        icon={Calculator}
        subtitle="Wrappers, allowances, and unrealised CGT — what to use first, what to defer"
        actions={
          <button type="button" onClick={fetchAll} className="btn-secondary gap-2 text-xs">
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} aria-hidden />
            Refresh
          </button>
        }
      />

      <TaxWorkspaceTabs />

      {/* Wrapper KPIs */}
      {wrappers && (
        <section>
          <h2 className="panel-title mb-3 flex items-center gap-2">
            <PiggyBank size={14} className="text-blue-light" aria-hidden />
            Wealth by Tax Wrapper
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 stagger-children">
            {wrappers.wrappers.map(w => (
              <div
                key={w.wrapper}
                className={clsx('card p-4 border', WRAPPER_COLOURS[w.wrapper] ?? 'border-border')}
              >
                <div className="flex items-baseline justify-between">
                  <p className="text-xs uppercase tracking-wider text-text-muted font-semibold">
                    {w.wrapper}
                  </p>
                  <p className="text-xs text-text-muted">{w.pct.toFixed(1)}%</p>
                </div>
                <p className="text-2xl font-bold text-text-primary mt-1">{fmt(w.value)}</p>
                <p className="text-[10px] text-text-muted mt-1">{w.description}</p>
                {w.allowance_label && (
                  <p className="text-[10px] text-blue-light mt-2 font-mono">{w.allowance_label}</p>
                )}
                {w.accounts.length > 0 && (
                  <details className="mt-2">
                    <summary className="text-[10px] text-text-muted cursor-pointer hover:text-text-secondary">
                      {w.accounts.length} account{w.accounts.length !== 1 ? 's' : ''}
                    </summary>
                    <ul className="mt-1 space-y-0.5">
                      {w.accounts.map(name => (
                        <li key={name} className="text-[10px] text-text-secondary">· {name}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Allowances */}
      {allowances && (
        <section>
          <h2 className="panel-title mb-3 flex items-center gap-2">
            <Receipt size={14} className="text-blue-light" aria-hidden />
            Tax Year Allowances ({allowances.tax_year})
          </h2>
          <p className="text-xs text-text-muted mb-3">
            Click <strong>edit</strong> on a card to record how much you've used — persists to DB,
            applies to wrapper cards above. Tax year auto-rolls on 6 Apr.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {allowances.allowances.map(a => (
              <AllowanceCard key={a.name} allowance={a} taxYear={allowances.tax_year} onChanged={fetchAll} />
            ))}
          </div>
        </section>
      )}

      {/* CGT exposure */}
      {cgt && (
        <section>
          <h2 className="panel-title mb-3 flex items-center gap-2">
            <TrendingUp size={14} className="text-blue-light" aria-hidden />
            Unrealised Capital Gains
          </h2>

          <div className="grid sm:grid-cols-3 gap-3 mb-4">
            <div className="stat-card">
              <p className="stat-label">Total unrealised gain</p>
              <p className={clsx(
                'stat-hero',
                cgt.total_unrealised_gain >= 0 ? 'text-emerald-light' : 'text-rose-light',
              )}>
                {fmtSigned(cgt.total_unrealised_gain)}
              </p>
              <p className="text-xs text-text-muted">across GIA + Crypto</p>
            </div>
            <div className="stat-card">
              <p className="stat-label">Taxable after £{cgt.cgt_allowance.toLocaleString('en-GB')} allowance</p>
              <p className="stat-value text-amber-light">
                {fmt(cgt.taxable_gain_after_allowance)}
              </p>
              <p className="text-xs text-text-muted">if disposed today</p>
            </div>
            <div className="stat-card">
              <p className="stat-label">
                Estimated CGT{cgt.applicable_cgt_rate_pct ? ` (${cgt.applicable_cgt_rate_pct}%)` : ' (18%/24%)'}
              </p>
              <p className="stat-value text-rose-light">
                {cgt.estimated_tax_applicable != null
                  ? fmt(cgt.estimated_tax_applicable)
                  : `${fmt(cgt.estimated_tax_basic_rate)}–${fmt(cgt.estimated_tax_higher_rate)}`}
              </p>
              <p className="text-xs text-text-muted">
                {cgt.applicable_rate_basis ? cgt.applicable_rate_basis : 'basic to higher rate'}
                {' · '}range {fmt(cgt.estimated_tax_basic_rate)}–{fmt(cgt.estimated_tax_higher_rate)}
              </p>
            </div>
          </div>

          {cgt.accounts.length > 0 && (
            <div className="card p-4">
              <h3 className="panel-title mb-3">
                Per-Account Unrealised Gains
              </h3>
              <div className="space-y-2">
                {cgt.accounts.map(acc => (
                  <div
                    key={acc.account_id}
                    className="flex items-center justify-between py-2 border-b border-border last:border-b-0"
                  >
                    <div>
                      <p className="text-sm font-medium text-text-primary">{acc.account_name}</p>
                      <p className="text-[10px] text-text-muted">
                        {acc.wrapper} · cost {fmt(acc.cost_basis)} → value {fmt(acc.current_value)}
                      </p>
                    </div>
                    <p className={clsx(
                      'text-sm font-bold',
                      acc.unrealised_gain >= 0 ? 'text-emerald-light' : 'text-rose-light',
                    )}>
                      {fmtSigned(acc.unrealised_gain)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {/* Pension flag for April 2027 */}
      <div className="card p-4 border-amber/30 bg-amber/5 flex items-start gap-3">
        <AlertTriangle size={16} className="text-amber shrink-0 mt-0.5" aria-hidden />
        <div className="text-xs text-text-secondary">
          <p className="text-text-primary font-semibold mb-1">Important: SIPP IHT changes April 2027</p>
          <p>
            From April 2027, defined-contribution pensions (SIPPs) will fall back inside the estate
            for Inheritance Tax. Given the size of your AJ Bell SIPP, this is a material planning
            event. See the IHT Estimator page for the impact.
          </p>
        </div>
      </div>
    </div>
  )
}

function AllowanceCard({
  allowance,
  taxYear,
  onChanged,
}: {
  allowance: Allowance
  taxYear: string
  onChanged: () => void | Promise<void>
}) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState<string>(String(allowance.used ?? 0))
  const [note, setNote] = useState<string>(allowance.note ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const pctUsed = allowance.annual_limit > 0 ? (allowance.used / allowance.annual_limit) * 100 : 0
  const canEdit = !!allowance.allowance_key

  const startEdit = () => {
    setValue(String(allowance.used ?? 0))
    setNote(allowance.note ?? '')
    setError(null)
    setEditing(true)
  }

  const save = async () => {
    if (!allowance.allowance_key) return
    const parsed = parseFloat(value)
    if (!Number.isFinite(parsed) || parsed < 0) {
      setError('Enter a non-negative number')
      return
    }
    setSaving(true)
    setError(null)
    try {
      await api.post('/api/tax/allowance-usage', {
        allowance_key: allowance.allowance_key,
        tax_year: taxYear,
        used_gbp: parsed,
        note: note || null,
      })
      setEditing(false)
      await onChanged()
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const reset = async () => {
    if (!allowance.allowance_key) return
    setSaving(true)
    setError(null)
    try {
      await api.delete(`/api/tax/allowance-usage?allowance_key=${allowance.allowance_key}&tax_year=${encodeURIComponent(taxYear)}`)
      await onChanged()
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'Reset failed')
    } finally {
      setSaving(false)
    }
  }

  const maxUsed = Math.max(allowance.annual_limit, 0)

  return (
    <div className="card p-4">
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-xs font-semibold text-text-primary">{allowance.name}</p>
        <p className="text-[10px] text-text-muted">{allowance.wrapper}</p>
      </div>
      <div className="flex items-baseline justify-between mt-2">
        <p className="text-lg font-bold text-text-primary">{fmt(allowance.remaining)}</p>
        <p className="text-[10px] text-text-muted">of {fmt(allowance.annual_limit)} left</p>
      </div>
      <div className="h-1.5 bg-surface-hover rounded-full overflow-hidden mt-2">
        <div
          className={clsx(
            'h-full transition-all',
            pctUsed > 80 ? 'bg-rose' : pctUsed > 50 ? 'bg-amber' : 'bg-emerald',
          )}
          style={{ width: `${Math.min(100, pctUsed)}%` }}
        />
      </div>

      {!editing ? (
        <>
          <div className="flex items-center justify-between mt-2 gap-2">
            <p className="text-[10px] text-text-muted">
              used <span className="font-mono text-text-secondary">{fmt(allowance.used)}</span>
            </p>
            {canEdit && (
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={startEdit}
                  className="text-[10px] px-2 py-0.5 rounded border border-blue-light/30 text-blue-light hover:bg-blue-light/10 transition"
                >
                  edit
                </button>
                {allowance.used > 0 && (
                  <button
                    type="button"
                    onClick={reset}
                    disabled={saving}
                    className="text-[10px] px-2 py-0.5 rounded border border-border text-text-muted hover:text-rose-300 hover:border-rose-500/30 transition disabled:opacity-50"
                    title="Reset to zero"
                  >
                    reset
                  </button>
                )}
              </div>
            )}
          </div>
          {allowance.note && (
            <p className="text-[10px] text-text-muted mt-1 italic">“{allowance.note}”</p>
          )}
        </>
      ) : (
        <div className="mt-2 space-y-2 bg-surface-hover/40 rounded p-2">
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-text-muted">Used £</span>
            <input
              type="number"
              min={0}
              max={maxUsed}
              step="0.01"
              value={value}
              onChange={e => setValue(e.target.value)}
              aria-label={`${allowance.name} used amount in GBP`}
              className="w-full mt-0.5 px-2 py-1 text-xs bg-surface-card border border-border rounded font-mono"
              autoFocus
            />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-text-muted">Note (optional)</span>
            <input
              type="text"
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="e.g. £20k into Vanguard FTSE All-World on 6 Apr"
              aria-label={`${allowance.name} usage note`}
              className="w-full mt-0.5 px-2 py-1 text-xs bg-surface-card border border-border rounded"
            />
          </label>
          {error && <p className="text-[10px] text-rose-light">{error}</p>}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="text-[10px] px-3 py-0.5 rounded bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/30 disabled:opacity-50"
            >
              {saving ? 'saving…' : 'save'}
            </button>
            <button
              type="button"
              onClick={() => { setEditing(false); setError(null); }}
              disabled={saving}
              className="text-[10px] px-3 py-0.5 rounded border border-border text-text-muted hover:text-text-primary"
            >
              cancel
            </button>
            <button
              type="button"
              onClick={() => setValue(String(allowance.annual_limit))}
              disabled={saving}
              className="ml-auto text-[10px] px-2 py-0.5 rounded border border-border text-text-muted hover:text-blue-light"
              title={`Set used to the full limit (${fmt(allowance.annual_limit)})`}
            >
              fill
            </button>
          </div>
        </div>
      )}

      <p className="text-[10px] text-text-secondary mt-2 leading-snug">
        {allowance.description}
      </p>
    </div>
  )
}
